import { wechat } from './api'
import {
  WX_APPID,
  WX_AUTH_CALLBACK_PATH,
} from '@/config/wechat'

/** 403/409：终态，带 message 进失败页 */
export const isAuthFailCode = (code) => code === 403 || code === 409

/** 从接口响应取出提示文案 */
const pickMsg = (res, fallback) =>
  res?.msg || res?.message || fallback
/**
 * 解析 silent-login 响应（仅 snsapi_base 静默阶段使用）
 * 已绑定: { code: 200, data: { token } }
 * 未绑定: { code: 200, data: { bound: false } } 或无 token
 * 终态: 403/409 → status: 'fail'
 * 可重试提示: 400 → status: 'toast'（由拦截器 toast）
 */
const parseSilentLoginResult = (res) => {
  if (!res) {
    return { status: 'fail', message: '微信授权失败' }
  }

  if (isAuthFailCode(res.code)) {
    return {
      status: 'fail',
      code: res.code,
      message: pickMsg(res, '无法登录'),
    }
  }

  // 400：仅 toast，回调里走通用失败页（不带业务文案）
  if (res.code === 400) {
    return { status: 'toast', code: 400 }
  }

  if (res.code !== 200) {
    return {
      status: 'fail',
      code: res.code,
      message: pickMsg(res, '微信授权失败'),
    }
  }

  const data = res.data || {}

  if (data.token) {
    return {
      status: 'bound',
      data: { token: data.token },
    }
  }

  if (data.status === 'bound' || data.status === 'unbound') {
    return {
      status: data.status,
      data: { token: data.token },
    }
  }

  if (data.bound === false || data.needBind) {
    return { status: 'unbound', data: {} }
  }

  return { status: 'fail', message: '微信授权响应异常' }
}

/** 绑定页上下文 key（userinfo code 已消耗，改存 openid/unionid） */
export const WX_BIND_CTX_KEY = 'wx_bind_ctx'

/** 构建微信公众号 OAuth 授权 URL */
export const buildWeChatOAuthUrl = (scope, state) => {
  const redirectUri = encodeURIComponent(`${location.origin}${WX_AUTH_CALLBACK_PATH}`)
  return (
    'https://open.weixin.qq.com/connect/oauth2/authorize' +
    `?appid=${WX_APPID}` +
    `&redirect_uri=${redirectUri}` +
    '&response_type=code' +
    `&scope=${scope}` +
    `&state=${state}` +
    '#wechat_redirect'
  )
}

/** 跳转微信 OAuth 授权页 */
export const redirectToWeChatOAuth = (scope, state) => {
  sessionStorage.setItem('wx_oauth_ing', state)
  window.location.assign(buildWeChatOAuthUrl(scope, state))
}

/** snsapi_base 静默回调：用 code 换 openid 并检查是否已绑定 */
export const wechatSilentLogin = async (code) => {
  const res = await wechat.silentLogin({ code })
  return parseSilentLoginResult(res)
}

/**
 * 解析 userinfo-login 响应（snsapi_userinfo 回调）
 * 已绑定(unionid): { code: 200, data: { status: 'bound', token } }
 * 未绑定: { code: 200, data: { status: 'unbound', openid, unionid } }
 */
const parseUserinfoLoginResult = (res) => {
  if (!res) {
    return { status: 'fail', message: '微信授权失败' }
  }

  if (isAuthFailCode(res.code)) {
    return {
      status: 'fail',
      code: res.code,
      message: pickMsg(res, '无法登录'),
    }
  }

  if (res.code === 400) {
    return { status: 'toast', code: 400 }
  }

  if (res.code !== 200) {
    return {
      status: 'fail',
      code: res.code,
      message: pickMsg(res, '微信授权失败'),
    }
  }

  const data = res.data || {}

  if (data.token || data.status === 'bound') {
    return {
      status: 'bound',
      data: { token: data.token },
    }
  }

  if (data.status === 'unbound') {
    return {
      status: 'unbound',
      data: {
        openid: data.wx_openid,
        unionid: data.unionid,
      },
    }
  }

  return { status: 'fail', message: '微信授权响应异常' }
}

/** snsapi_userinfo 回调：用 code 换 unionid 再查绑定，未绑定则返回 openid/unionid */
export const wechatUserinfoLogin = async (code) => {
  const res = await wechat.userinfoLogin({ code })
  return parseUserinfoLoginResult(res)
}
